import React, { useState, useEffect } from 'react';
import { Brain, AlertCircle, CheckCircle, Moon, Sun, HelpCircle, History } from 'lucide-react';
import FileUpload from './components/FileUpload';
import ResultDisplay from './components/ResultDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import HelpModal from './components/HelpModal';
import HistoryManager from './components/HistoryManager';
import { ApiService } from './services/api';
import { PDFAnalysisData } from './types';

type ProcessingType = 'analysis' | null;

function App() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [analysisData, setAnalysisData] = useState<PDFAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processingType, setProcessingType] = useState<ProcessingType>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Initialize dark mode as default
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    
    if (savedTheme === 'light') {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark');
    } else {
      // Default to dark mode
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setAnalysisData(null);
    setError('');
    setSuccess('');
  };

  const processFile = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setIsLoading(true);
    setProcessingType('analysis');
    setError('');
    setSuccess('');

    try {
      const response = await ApiService.uploadAndAnalyzePDF(selectedFile);
      if (response.success && response.data) {
        setAnalysisData(response.data);
        setSuccess('PDF analysis completed successfully!');
      } else {
        setError(response.error || 'Analysis failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Processing error:', err);
    } finally {
      setIsLoading(false);
      setProcessingType(null);
    }
  };

  const clearResults = () => {
    setAnalysisData(null);
    setSelectedFile(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white shadow-sm border-b dark:bg-gray-800 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">PDF Reader</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1">Upload and extract text from PDF files</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowHistoryModal(true)}
                className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
                aria-label="Show history"
                title="Xem lịch sử phân tích"
              >
                <History className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowHelpModal(true)}
                className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                aria-label="Show help"
                title="Hướng dẫn sử dụng"
              >
                <HelpCircle className="h-5 w-5" />
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>API Connected</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/20 dark:border-red-800">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/20 dark:border-green-800">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-green-800 dark:text-green-200">{success}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Upload PDF</h2>
              <FileUpload
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                isLoading={isLoading}
              />
            </div>

            {/* Action Buttons */}
            {selectedFile && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Process PDF</h3>
                <div className="space-y-3">
                  <button
                    onClick={processFile}
                    disabled={isLoading}
                    className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading && processingType === 'analysis' ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Brain className="h-4 w-4" />
                    )}
                    <span>Analyze Content</span>
                  </button>

                  <button
                    onClick={clearResults}
                    disabled={isLoading}
                    className="w-full text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 py-2 px-4 rounded-lg border border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear Results
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {analysisData && (
              <ResultDisplay
                data={analysisData}
                type="analysis"
                isLoading={false}
              />
            )}

            {!analysisData && !isLoading && (
              <div className="card">
                <div className="text-center py-12">
                  <Brain className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No Results Yet</h3>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">
                    Upload a PDF file and click "Analyze Content" to see results here.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16 dark:bg-gray-800 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
            <p>PDF Reader with OpenAI Integration • Built with React, TypeScript, and Tailwind CSS</p>
          </div>
        </div>
      </footer>

      {/* Help Modal */}
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />

      {/* History Modal */}
      <HistoryManager 
        isOpen={showHistoryModal} 
        onClose={() => setShowHistoryModal(false)} 
      />
    </div>
  );
}

export default App;
